import type { PortalDirectoryContext } from '@/modules/client-portal/directory.api'
import { useMemo } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'

import { isValidUUID } from '@scrambl/shared'
import {
  linkAbortSignals,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

/** Account-wide discovery is valid outside an entity route or staff workspace. */
export function useClientPortalDirectorySession() {
  const workspace = useWorkspaceSession()
  return useMemo(() => {
    const enabled =
      !!workspace.ownerUserId && isValidUUID(workspace.ownerUserId)
    const assertCurrent = () => {
      workspace.assertUnchanged()
      if (!enabled || workspace.signal.aborted) throw new SessionChangedError()
    }
    return {
      enabled,
      key: workspace.key,
      assertCurrent,
      async run<T>(
        signal: AbortSignal,
        action: (context: PortalDirectoryContext) => Promise<T>,
      ): Promise<T> {
        assertCurrent()
        const linked = linkAbortSignals([signal, workspace.signal])
        try {
          linked.signal.throwIfAborted()
          const value = await action({
            userId: workspace.ownerUserId!,
            signal: linked.signal,
          })
          assertCurrent()
          linked.signal.throwIfAborted()
          return value
        } finally {
          linked.dispose()
        }
      },
    }
  }, [workspace])
}
